import {
  Autocomplete,
  FormControl,
  TextField,
  Typography,
  AutocompleteProps,
} from "@mui/material";
import { Control, Controller, FieldPath, FieldValues } from "react-hook-form";
import { APP_COLORS, APP_FONT_FAMILIES } from "../../../enums";
import { FormHelperTextError } from "../errors";

interface FormAutocompleteProps<T extends FieldValues> {
  name: FieldPath<T>;
  control: Control<T>;
  label: string;
  required?: boolean;
  error?: string;
  helperText?: string;
  options?: any[];
  loading?: boolean;
  freeSolo?: boolean;
  getOptionLabel?: (option: any) => string;
  isOptionEqualToValue?: (option: any, value: any) => boolean;
  valueField?: string;
  labelField?: string;
  autocompleteProps?: Partial<AutocompleteProps<any, boolean, boolean, boolean>>;
  formControlProps?: any;
  labelProps?: any;
}

function FormAutocomplete<T extends FieldValues>({
  name,
  control,
  label,
  required = true,
  error,
  helperText,
  options = [],
  loading = false,
  freeSolo = false,
  getOptionLabel,
  isOptionEqualToValue,
  valueField = "id",
  labelField = "label",
  autocompleteProps = {},
  formControlProps = {},
  labelProps = {},
}: FormAutocompleteProps<T>) {
  const fieldId = `form-field-${name}`;
  const errorId = `component-error-text-${name}`;

  const defaultGetOptionLabel = (option: any) => {
    if (typeof option === "string") return option;
    return option?.[labelField] || "";
  };

  const defaultIsOptionEqualToValue = (option: any, value: any) => {
    if (typeof option === "string" && typeof value === "string") {
      return option === value;
    }
    return option?.[valueField] === value?.[valueField] || option?.[valueField] === value;
  };

  return (
    <FormControl
      variant="outlined"
      margin="dense"
      fullWidth
      {...formControlProps}
    >
      <Typography
        variant="body"
        component="span"
        fontWeight={600}
        fontFamily={APP_FONT_FAMILIES.ibmPlexMono}
        color={APP_COLORS.textFieldLabelBlack}
        mb={0.5}
        sx={{ ...labelProps?.sx }}
      >
        {label}
        {required && <Typography component="span" color={APP_COLORS.error}>*</Typography>}
      </Typography>
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          let displayValue: any = null;

          if (freeSolo) {
            displayValue = field.value || null;
          } else {
            const selectedOption = options?.find(
              (opt) => opt?.[valueField] === field.value
            );
            displayValue = selectedOption || null;
          }

          return (
            <Autocomplete
              options={options}
              value={displayValue}
              loading={loading}
              freeSolo={freeSolo}
              getOptionLabel={getOptionLabel || defaultGetOptionLabel}
              isOptionEqualToValue={
                isOptionEqualToValue || defaultIsOptionEqualToValue
              }
              onChange={(event, newValue) => {
                if (freeSolo) {
                  if (typeof newValue === "string") {
                    field.onChange(newValue);
                  } else if (newValue) {
                    field.onChange(newValue[labelField] || "");
                  } else {
                    field.onChange("");
                  }
                } else {
                  if (newValue) {
                    field.onChange(newValue[valueField]);
                  } else {
                    field.onChange("");
                  }
                }
              }}
              onInputChange={(event, newInputValue, reason) => {
                if (freeSolo && reason === "input") {
                  field.onChange(newInputValue);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  id={fieldId}
                  hiddenLabel
                  variant="outlined"
                  size="small"
                  fullWidth
                  error={!!error}
                  aria-describedby={errorId}
                />
              )}
              {...autocompleteProps}
            />
          );
        }}
      />
      <FormHelperTextError
        id={errorId}
        error={error}
        helperText={helperText}
      />
    </FormControl>
  );
}

export { FormAutocomplete };
